import React, { useEffect, useState, useRef } from "react";
import api from "../../../utils/axios";
import SearchableSelect from "../SearchableSelect";
import {
  insertData,
  getAlert,
  useEscapeClose,
  getDataWithComp,
} from "../../../utils/SamuiUtils";
import { useFormFieldArrowNavigation } from "../../../utils/ModalKeyboardNavigation";

const buildDefaultForm = () => ({
  AP_Id: "",
  Comp_Id: window.localStorage.getItem("company") || "",
  AP_Code: "",
  AP_Name: "",
  AP_Type: 1,
  AP_ShortName: "",
  AP_TaxNo: "",
  AP_Add1: "",
  AP_Add2: "",
  AP_Add3: "",
  AP_Province: "",
  AP_Zipcode: "",
  AP_Contact: "",
  AP_Tel: "",
  AP_Status: "Y",
  AP_Credit: 0,
  AP_Vat: 1,
  AP_Discount: 0,
  AP_Branch: "",
  AP_BT_Name: "",
  Bank_Id: null,
  Bank_Acc_No: "",
  Bank_Acc_Name: "",
});

const CreditorQuickCreateModal = ({ show, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(buildDefaultForm());
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [selProvinceId, setSelProvinceId] = useState("");
  const [selDistrictId, setSelDistrictId] = useState("");
  const [selSubdistrictId, setSelSubdistrictId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  useEscapeClose(show, () => {
    if (!isSaving) onClose?.();
  });

  // เปิดใช้งานการเลื่อน focus ระหว่างฟิลด์ด้วยปุ่มลูกศรเมื่อ modal แสดง
  useFormFieldArrowNavigation(show);

  useEffect(() => {
    if (!show) return;
    setFormData(buildDefaultForm());
    setSelProvinceId("");
    setSelDistrictId("");
    setSelSubdistrictId("");
    setDistricts([]);
    setSubdistricts([]);
  }, [show]);

  useEffect(() => {
    api
      .get("/provinces")
      .then((res) => setProvinces(res.data.data || []))
      .catch((error) =>
        console.error("Failed to load provinces for creditor modal:", error)
      );
  }, []);

  const resetLocation = () => {
    setSelProvinceId("");
    setSelDistrictId("");
    setSelSubdistrictId("");
    setDistricts([]);
    setSubdistricts([]);
  };

  const handleProvinceChange = async (value, option) => {
    setSelProvinceId(value);
    setFormData((prev) => ({
      ...prev,
      AP_Province: option?.label || "",
      AP_Add3: "",
      AP_Add2: "",
      AP_Zipcode: "",
    }));
    setDistricts([]);
    setSubdistricts([]);
    setSelDistrictId("");
    setSelSubdistrictId("");
    if (!value) return;
    try {
      const res = await api.get(`/districts/${value}`);
      setDistricts(res.data.data || []);
    } catch (error) {
      console.error("Failed to load districts:", error);
    }
  };

  const handleDistrictChange = async (value, option) => {
    setSelDistrictId(value);
    setFormData((prev) => ({
      ...prev,
      AP_Add3: option?.label || "",
      AP_Add2: "",
      AP_Zipcode: "",
    }));
    setSubdistricts([]);
    setSelSubdistrictId("");
    if (!value) return;
    try {
      const res = await api.get(`/subdistricts/${value}`);
      setSubdistricts(res.data.data || []);
    } catch (error) {
      console.error("Failed to load subdistricts:", error);
    }
  };

  const handleSubdistrictChange = (value, option) => {
    const selected = subdistricts.find(
      (s) => String(s.SubdistrictId) === String(value)
    );
    setSelSubdistrictId(value);
    setFormData((prev) => ({
      ...prev,
      AP_Add2: option?.label || (selected ? selected.NameInThai : prev.AP_Add2),
      AP_Zipcode: selected ? selected.ZipCode : prev.AP_Zipcode,
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    const formatPhone = (v) => {
      const digits = String(v).replace(/\D/g, "").slice(0, 10);
      if (digits.length <= 3) return digits;
      return digits.slice(0, 3) + '-' + digits.slice(3);
    };

    const formatTaxId = (v) => {
      let d = String(v).replace(/\D/g, "").slice(0, 13);
      const parts = [];
      const pattern = [1, 4, 5, 2, 1];
      let pos = 0;
      for (let len of pattern) {
        if (pos >= d.length) break;
        const take = Math.min(len, d.length - pos);
        parts.push(d.substr(pos, take));
        pos += take;
      }
      return parts.join('-');
    };

    if (name === 'AP_Tel') {
      const formatted = formatPhone(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    if (name === 'AP_TaxNo') {
      const formatted = formatTaxId(value);
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (savingRef.current) return; // ป้องกันการเรียกซ้ำ ๆ ขณะกำลังบันทึก

    if (!formData.AP_Code.trim()) {
      getAlert("WARNING", "โปรดระบุรหัสผู้ขาย");
      return;
    }
    if (!formData.AP_Name.trim()) {
      getAlert("WARNING", "โปรดระบุชื่อผู้ขาย");
      return;
    }

    // ตรวจสอบเลขประจำตัวผู้เสียภาษี: ถ้ามี ให้ยอมรับเฉพาะตัวเลขและ - และจำนวนตัวเลขต้องเท่ากับ 13
    if (formData.AP_TaxNo) {
      const digitsOnly = String(formData.AP_TaxNo).replace(/[^0-9]/g, "");
      if (digitsOnly.length !== 13) {
        getAlert("WARNING", "เลขประจำตัวผู้เสียภาษีต้องมีตัวเลข 13 หลัก (ไม่รวมยัติภังค์)");
        return;
      }
    }

    try {
      savingRef.current = true;
      setIsSaving(true);
      // ตรวจสอบรหัสผู้ขายซ้ำ
      try {
        const existed = await getDataWithComp(
          "Tb_Set_AP",
          `AND AP_Code = '${formData.AP_Code.trim()}'`
        );
        if (existed && existed.length > 0) {
          getAlert("FAILED", "มีรหัสผู้ขายนี้อยู่ในระบบแล้ว");
          setIsSaving(false);
          return;
        }
      } catch (err) {
        console.error("Error checking existing AP code:", err);
      }
      const { AP_Id, ...payload } = formData;
      payload.AP_Status = "Y";
      const fields = Object.keys(payload).join(", ");
      const values = Object.values(payload);
      const response = await insertData("Tb_Set_AP", fields, values);
      if (response?.status === "FAILED") {
        getAlert(
          "FAILED",
          response?.message || "ไม่สามารถสร้างข้อมูลผู้ขายได้ กรุณาลองใหม่"
        );
        return;
      }

      let insertedAp = null;

      if (response?.result && response.result.recordset && response.result.recordset.length > 0) {
        insertedAp = response.result.recordset[0];
      }

      resetLocation();
      setFormData(buildDefaultForm());
      onSuccess?.(insertedAp);
      onClose?.();
    } catch (error) {
      console.error("Error saving creditor:", error);
      getAlert("FAILED", error?.message || "เกิดข้อผิดพลาดขณะบันทึกข้อมูล");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className={`modal ${show ? "show" : ""}`}
        style={{ display: show ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">สร้างผู้ขายใหม่</h5>
              <button
                type="button"
                className="close"
                onClick={() => {
                  if (!savingRef.current && !isSaving) onClose?.();
                }}
                aria-label="Close"
                disabled={isSaving}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">รหัสผู้ขาย <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_Code"
                    value={formData.AP_Code}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">ชื่อผู้ขาย <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_Name"
                    value={formData.AP_Name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">เลขประจำตัวผู้เสียภาษี</label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_TaxNo"
                    value={formData.AP_TaxNo}
                    onChange={handleChange}
                    maxLength={17}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">โทรศัพท์</label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_Tel"
                    value={formData.AP_Tel}
                    onChange={handleChange}
                    maxLength={11}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">ที่อยู่</label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_Add1"
                    value={formData.AP_Add1}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">จังหวัด</label>
                  <SearchableSelect
                    value={selProvinceId}
                    options={provinces.map((p) => ({
                      value: p.ProvinceId,
                      label: p.NameInThai,
                    }))}
                    placeholder="เลือกจังหวัด"
                    searchPlaceholder="ค้นหาจังหวัด"
                    onChange={handleProvinceChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">อำเภอ</label>
                  <SearchableSelect
                    value={selDistrictId}
                    options={districts.map((d) => ({
                      value: d.DistrictId,
                      label: d.NameInThai,
                    }))}
                    placeholder="เลือกอำเภอ"
                    searchPlaceholder="ค้นหาอำเภอ"
                    disabled={!districts.length}
                    onChange={handleDistrictChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">ตำบล</label>
                  <SearchableSelect
                    value={selSubdistrictId}
                    options={subdistricts.map((s) => ({
                      value: s.SubdistrictId,
                      label: s.NameInThai,
                    }))}
                    placeholder="เลือกตำบล"
                    searchPlaceholder="ค้นหาตำบล"
                    disabled={!subdistricts.length}
                    onChange={handleSubdistrictChange}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">รหัสไปรษณีย์</label>
                  <input
                    type="text"
                    className="form-control"
                    name="AP_Zipcode"
                    value={formData.AP_Zipcode}
                    onChange={handleChange}
                  />
                </div>

              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-danger text-white"
                onClick={() => onClose?.()}
                disabled={isSaving}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="btn btn-primary text-white"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? "กำลังบันทึก..." : "บันทึกผู้ขาย"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal-backdrop fade ${show ? "show" : ""}`}
        style={{ display: show ? "block" : "none" }}
      />
    </>
  );
};

export default CreditorQuickCreateModal;
